import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Menu, X, User, ChevronDown, MapPin, Phone, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from './ui/dialog';
import PostPropertyModal from './PostPropertyModal';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [postOpen, setPostOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-100">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-[#9a1b2f] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 md:w-6 md:h-6 text-white" strokeWidth={2.5} />
            </div>
            <span className="text-lg md:text-xl font-bold text-[#9a1b2f] tracking-tight">SafeHouse</span>
          </Link>

          {/* Search bar - Desktop */}
          <div className="hidden md:flex flex-1 max-w-md mx-6">
            <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
              <DialogTrigger asChild>
                <button className="w-full flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-full hover:border-[#9a1b2f] hover:shadow-sm transition-all bg-gray-50">
                  <Search className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-500">Search by builder, project, location...</span>
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl p-0 gap-0">
                <div className="p-4 border-b">
                  <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3">
                    <Search className="w-4 h-4 text-gray-400" />
                    <Input placeholder="Search projects, builders, locations" className="border-0 focus-visible:ring-0" />
                  </div>
                </div>
                <div className="p-4 max-h-96 overflow-y-auto">
                  <h3 className="text-sm font-semibold text-gray-500 mb-3">Popular Searches</h3>
                  <div className="flex flex-wrap gap-2">
                    {['M3M Crown', 'DLF Privana', 'Godrej Zenith', 'Eldeco Acclaim', 'Sector 89', 'Whitefield'].map((s) => (
                      <button key={s} className="px-3 py-1.5 text-xs rounded-full bg-gray-100 hover:bg-[#9a1b2f] hover:text-white transition-colors">{s}</button>
                    ))}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Right nav */}
          <div className="flex items-center gap-2 md:gap-3">
            <div className="hidden lg:flex items-center gap-1">
              <NavDropdown label="Looking For" items={[
                { label: 'Buy', href: '/listings?type=resale' },
                { label: 'Rent', href: '/listings?type=rent' },
                { label: 'Sell', href: '#' },
              ]} />
              <NavDropdown label="Listings" items={[
                { label: 'Resale Properties', href: '/listings?type=resale' },
                { label: 'Rent Properties', href: '/listings?type=rent' },
                { label: 'Resale Commercial', href: '/listings?type=resale_commercial' },
                { label: 'Rent Commercial', href: '/listings?type=rent_commercial' },
              ]} />
              <NavDropdown label="Services" items={[
                { label: 'Home Loan', href: '/#services' },
                { label: 'Home Inspection', href: '/#inspection' },
                { label: 'Legal Advice', href: '/#services' },
                { label: 'Taxation Advice', href: '/#services' },
              ]} />
              <a href="#" className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-[#9a1b2f] transition-colors">Developers</a>
            </div>

            <Button
              onClick={() => setPostOpen(true)}
              className="hidden md:inline-flex bg-[#9a1b2f] hover:bg-[#7a1524] text-white rounded-full px-5 h-9 text-sm font-semibold"
            >
              Post Property
            </Button>

            <button className="hidden md:flex w-9 h-9 rounded-full border border-gray-200 items-center justify-center hover:border-[#9a1b2f] transition-colors">
              <User className="w-4 h-4 text-gray-600" />
            </button>

            <button
              className="lg:hidden w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5 text-gray-700" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/40" onClick={() => setMobileMenuOpen(false)}>
          <div className="absolute right-0 top-0 h-full w-80 bg-white shadow-xl p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <span className="text-lg font-bold text-[#9a1b2f]">Menu</span>
              <button onClick={() => setMobileMenuOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <nav className="flex flex-col gap-1">
              {[
                { l: 'Buy', to: '/listings?type=resale' },
                { l: 'Rent', to: '/listings?type=rent' },
                { l: 'Resale Properties', to: '/listings?type=resale' },
                { l: 'Rent Properties', to: '/listings?type=rent' },
                { l: 'Resale Commercial', to: '/listings?type=resale_commercial' },
                { l: 'Rent Commercial', to: '/listings?type=rent_commercial' },
                { l: 'Home Loan', to: '/#services' },
                { l: 'Home Inspection', to: '/#inspection' },
              ].map((item) => (
                <Link key={item.l} to={item.to} onClick={() => setMobileMenuOpen(false)} className="py-3 px-3 rounded-md hover:bg-gray-50 text-gray-800 text-sm font-medium border-b border-gray-100">{item.l}</Link>
              ))}
              <button onClick={() => { setMobileMenuOpen(false); setPostOpen(true); }} className="text-left py-3 px-3 rounded-md hover:bg-gray-50 text-gray-800 text-sm font-medium border-b border-gray-100">
                Post / Sell Property
              </button>
            </nav>
            <Button onClick={() => { setMobileMenuOpen(false); setPostOpen(true); }} className="w-full mt-6 bg-[#9a1b2f] hover:bg-[#7a1524] text-white rounded-full">Post Property</Button>
            <div className="mt-6 text-xs text-gray-500 flex items-center gap-2"><Phone className="w-3 h-3" /> +91 98100 00000</div>
          </div>
        </div>
      )}
      <PostPropertyModal open={postOpen} onOpenChange={setPostOpen} />
    </header>
  );
};

const NavDropdown = ({ label, items }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-[#9a1b2f] transition-colors">
        {label}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 pt-2 min-w-[200px] z-50">
          <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-2 overflow-hidden">
            {items.map((item) => (
              item.href.startsWith('/') && !item.href.includes('#') ? (
                <Link key={item.label} to={item.href} className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#fef2f4] hover:text-[#9a1b2f] transition-colors">
                  {item.label}
                </Link>
              ) : (
                <a key={item.label} href={item.href} className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#fef2f4] hover:text-[#9a1b2f] transition-colors">
                  {item.label}
                </a>
              )
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;
